import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Brain, Users, BookOpen, Lightbulb, BarChart3, ArrowLeft } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead title="About O4S.ai — AI-Powered Career Guidance" path="/about" />

      <nav className="glass-nav sticky top-0 z-50 px-4 md:px-8 py-3 border-b border-earth-200">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 text-earth-500 hover:text-earth-700 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Target className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-earth-700">O4S.ai</span>
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-16">
        <h1 className="text-3xl sm:text-4xl font-bold text-earth-700 mb-2">About O4S.ai</h1>
        <p className="text-earth-500 text-lg mb-10">Opportunities for Students — AI-Powered Career Guidance</p>

        <section className="mb-10">
          <h2 className="text-xl font-bold text-earth-700 mb-3">What is O4S.ai?</h2>
          <p className="text-earth-600 leading-relaxed mb-4">O4S.ai is an AI-powered career guidance platform built specifically for Indian students. We help students from 10th class through post-graduation make informed career decisions using personalised AI analysis, expert-curated FAQs, and detailed career roadmaps.</p>
          <p className="text-earth-600 leading-relaxed">Our platform focuses on the Andhra Pradesh and Telangana regions, providing locally relevant insights including city-level job market data, realistic salary ranges in Indian Rupees, and region-specific educational opportunities.</p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-bold text-earth-700 mb-4">What We Offer</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: Brain, title: 'AI Career Assessment', desc: 'A 5-step personalised assessment that analyses your education, skills, and goals to recommend career paths.' },
              { icon: BarChart3, title: 'Skill2Income Engine', desc: 'Discover your earning potential with a detailed income roadmap, 3 earning paths, and a 90-day action plan.' },
              { icon: BookOpen, title: '1,890+ Expert FAQs', desc: 'Region-specific career questions answered with local insights, course suggestions, and salary data.' },
              { icon: Lightbulb, title: '106 Career Roadmaps', desc: 'Detailed career paths with salary charts, learning tiers, and growth prospects across 11 industries.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl border border-earth-200 p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
                    <item.icon className="w-4 h-4 text-primary" />
                  </div>
                  <h3 className="font-bold text-earth-700">{item.title}</h3>
                </div>
                <p className="text-sm text-earth-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-bold text-earth-700 mb-3">Our Mission</h2>
          <p className="text-earth-600 leading-relaxed">We believe every student deserves access to quality career guidance, regardless of their background. By combining artificial intelligence with expert knowledge, we aim to bridge the gap between education and employability in India.</p>
        </section>

        <div className="bg-surface-alt rounded-xl border border-earth-200 p-6 text-center">
          <Users className="w-6 h-6 text-primary mx-auto mb-2" />
          <p className="text-earth-600 italic text-sm leading-relaxed">"I used O4S.ai for my career growth, and the AI-guided recommendations were extremely helpful, a must-try tool for taking your career to the next level."</p>
        </div>
      </main>

      <footer className="px-4 md:px-8 py-8 bg-earth-700 text-white mt-auto">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm mb-4">
            <Link to="/about" className="text-earth-400 hover:text-white transition-colors">About</Link>
            <Link to="/founder" className="text-earth-400 hover:text-white transition-colors">Founder</Link>
            <Link to="/contact" className="text-earth-400 hover:text-white transition-colors">Contact</Link>
            <Link to="/privacy" className="text-earth-400 hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="text-earth-400 hover:text-white transition-colors">Terms</Link>
            <Link to="/legal" className="text-earth-400 hover:text-white transition-colors">Legal</Link>
          </div>
          <p className="text-earth-500 text-xs text-center">&copy; 2026 O4S.ai. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
